// Tiny typed fetch wrapper.
//
// Auth-header strategy depends on the active mode (see lib/auth-mode.ts):
//   - 'msal' : Authorization: Bearer <accessToken> from MSAL silent acquire
//   - 'dev'  : X-Dev-User: <email> from the auth store (legacy dev flow)
//
// MSAL silent acquire is cheap on cache hits (~5 ms) and is the only path
// that handles token expiry transparently. On InteractionRequired (cache
// miss / refresh failure / user logged out from MS side), we kick off
// loginRedirect — which navigates away from the SPA, so the in-flight
// fetch never returns.
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { currentAuthMode } from '@/lib/auth-mode';
import { loginRequest, pca, silentRequestTemplate } from '@/lib/msal';
import { useAuthStore } from '@/store/auth';
export class ApiError extends Error {
    status;
    details;
    constructor(status, message, details) {
        super(message);
        this.status = status;
        this.details = details;
    }
}
// In local dev, vite.config.ts proxies /api/* to the backend container, so
// a relative URL works. In cloud builds, VITE_API_BASE_URL is inlined at
// build time (set by .github/workflows/deploy.yml from the App Service
// hostname) and we hit the backend cross-origin — backend's CORS allow-list
// is wired in main.bicep to permit the SWA's default hostname.
const BASE = `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1`;
/**
 * Build the request-time auth header for protected endpoints.
 *
 * Resolution order:
 *   1. If `devUser` is set in the store, always attach `X-Dev-User`. This
 *      preserves the "Dev sign-in (bypass Microsoft)" affordance even
 *      when the build is configured for MSAL. The dev-user path only
 *      activates when the user explicitly chose it, so it can't collide
 *      with a real MSAL session.
 *   2. Otherwise in MSAL mode, silently acquire a token and send the
 *      **id_token** as the bearer (NOT the access token — see lib/msal.ts
 *      for the rationale; backend validates aud=clientId on id_tokens).
 *   3. Otherwise (dev build with no devUser yet), no header.
 *
 * NOT used for the /csat/{token} public endpoint (see publicGet/publicPost).
 */
async function authHeader(forceRefresh = false) {
    // Super-Admin "View as" — sent alongside the normal auth header on every
    // request. The backend honours it ONLY when the real principal is a Super
    // Admin, so it can never escalate; it only changes which user we act as.
    const impId = useAuthStore.getState().impersonateUserId;
    const imp = impId ? { 'X-Impersonate-User': impId } : {};
    const devUser = useAuthStore.getState().devUser;
    if (devUser) {
        return { 'X-Dev-User': devUser, ...imp };
    }
    if (currentAuthMode() === 'msal') {
        if (!pca)
            return { ...imp };
        const account = pca.getActiveAccount();
        if (!account)
            return { ...imp };
        try {
            const result = await pca.acquireTokenSilent({
                ...silentRequestTemplate,
                account,
                // forceRefresh bypasses MSAL's cache and uses the refresh token
                // to mint a fresh id_token. Used by the 401 retry path in
                // request() below — without it, MSAL happily returns a cached
                // id_token that the backend has already rejected as expired
                // (the 2026-05-24 invite-user "Bearer ... Signature has expired"
                // incident — see EOD memory for full context).
                forceRefresh,
            });
            // For an SPA + backend sharing one app registration, we send the
            // **id_token** as the bearer (see lib/msal.ts for the full
            // rationale). The backend validates `aud = clientId` and
            // `iss = v2.0/<tenant>`, both present on the id_token.
            return { Authorization: `Bearer ${result.idToken}`, ...imp };
        }
        catch (err) {
            // Cache miss, refresh-token failure, or user signed out from the MS
            // side. The only way back is full interactive login — which
            // navigates away, so this fetch never returns to the caller.
            if (err instanceof InteractionRequiredAuthError) {
                await pca.acquireTokenRedirect(loginRequest);
            }
            else {
                console.error('[api] acquireTokenSilent failed:', err);
            }
            return { ...imp };
        }
    }
    return { ...imp };
}
/**
 * Build a fully-qualified URL for an EventSource (SSE) subscription.
 *
 * EventSource cannot send custom headers, so the auth token has to ride
 * in the query string. This helper returns the BASE + path + `?token=`
 * (or `?dev_user=` for dev mode) so callers can:
 *
 *     const url = await eventSourceUrl(`/tickets/${id}/events`);
 *     const es = new EventSource(url);
 *
 * Returns null when no auth is available — caller should skip opening
 * the stream rather than hit an unauthenticated URL (which would 401
 * → EventSource auto-reconnect spin).
 *
 * Sprint 1.5 (§14.14).
 */
export async function eventSourceUrl(path) {
    const devUser = useAuthStore.getState().devUser;
    if (devUser) {
        return `${BASE}${path}?dev_user=${encodeURIComponent(devUser)}`;
    }
    if (currentAuthMode() === 'msal') {
        if (!pca)
            return null;
        const account = pca.getActiveAccount();
        if (!account)
            return null;
        try {
            const result = await pca.acquireTokenSilent({
                ...silentRequestTemplate,
                account,
            });
            return `${BASE}${path}?token=${encodeURIComponent(result.idToken)}`;
        }
        catch (err) {
            // Don't surface the token-acquire failure as a runtime error —
            // the live-update path is non-essential. Caller skips opening
            // the EventSource and the page still works without realtime.
            console.warn('[sse] could not acquire token for EventSource:', err);
            return null;
        }
    }
    return null;
}
async function request(method, path, body, extraHeaders) {
    return doRequest(method, path, body, extraHeaders, /*forceRefresh*/ false);
}
async function doRequest(method, path, body, extraHeaders, forceRefresh) {
    const auth = await authHeader(forceRefresh);
    const headers = {
        'Content-Type': 'application/json',
        ...auth,
        ...(extraHeaders ?? {}),
    };
    const init = { method, headers };
    if (body !== undefined)
        init.body = JSON.stringify(body);
    const res = await fetch(`${BASE}${path}`, init);
    if (res.status === 204)
        return undefined;
    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    }
    catch {
        data = text;
    }
    if (!res.ok) {
        const errObj = data?.error;
        // Some auth failures must NOT trigger MSAL's silent-refresh retry:
        //   • SESSION_REVOKED — admin (or this user from another device)
        //     called /auth/logout-all. Silent-refresh would mint a fresh
        //     iat > cutoff and defeat the revocation. Force a hard logout.
        //   • MFA_REQUIRED — MSAL can't elevate the amr claim without a
        //     fresh interactive sign-in, so silent refresh is futile.
        if (res.status === 401 && errObj?.code === 'SESSION_REVOKED') {
            handleSessionRevoked();
            throw new ApiError(res.status, errObj.message ?? 'Session revoked.', errObj.details);
        }
        if (res.status === 403 && errObj?.code === 'MFA_REQUIRED') {
            handleMfaRequired();
            throw new ApiError(res.status, errObj.message ?? 'MFA required.', errObj.details);
        }
        // Otherwise: 401 once → force-refresh the bearer (cached id_token
        // may have expired) and retry exactly once. If the second attempt
        // also 401s the user genuinely isn't authenticated — surface the
        // error and let the UI prompt re-login. Standard SPA pattern.
        if (res.status === 401 && !forceRefresh) {
            return doRequest(method, path, body, extraHeaders, /*forceRefresh*/ true);
        }
        const message = errObj?.message ?? `HTTP ${res.status}`;
        throw new ApiError(res.status, message, errObj?.details ?? data);
    }
    return data;
}
// Hard-redirect helpers — these must short-circuit any retry logic and
// drop the user on /login (revoked) or /auth/mfa-required (MFA missing).
// Kept side-effect-free at module load; only fire when called.
let _signoutInFlight = false;
function handleSessionRevoked() {
    if (_signoutInFlight)
        return;
    _signoutInFlight = true;
    // Clear the local store.
    void import('@/store/auth').then(({ useAuthStore }) => {
        useAuthStore.getState().setMe(null);
        useAuthStore.getState().setDevUser(null);
    });
    // CRITICAL: also drop MSAL's cached token, then redirect. Without the
    // clear, the cached (old-iat) token survives the full-page reload:
    // LoginPage's "already signed in → navigate('/')" effect forwards straight
    // back into AppShell, /me 401s SESSION_REVOKED again, and the app
    // ping-pongs between /login and / forever (the "continuously loading"
    // symptom). clearCache() removes only the SPA's local MSAL session
    // (sessionStorage), NOT the Entra IdP session — so a fresh interactive/SSO
    // sign-in mints a new token whose `iat` is after the logout-everywhere
    // cutoff and works. We redirect whether the clear succeeds or fails so a
    // transient MSAL hiccup can't strand the user.
    const go = () => {
        window.location.href = '/login?reason=session_revoked';
    };
    if (pca) {
        void pca.clearCache().then(go, go);
    }
    else {
        go();
    }
}
function handleMfaRequired() {
    if (_signoutInFlight)
        return;
    _signoutInFlight = true;
    window.location.href = '/login?reason=mfa_required';
}
async function requestForm(path, form, forceRefresh = false) {
    // Do NOT set Content-Type — fetch fills in the multipart boundary itself.
    const auth = await authHeader(forceRefresh);
    const headers = { ...auth };
    const res = await fetch(`${BASE}${path}`, { method: 'POST', headers, body: form });
    if (res.status === 204)
        return undefined;
    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    }
    catch {
        data = text;
    }
    if (!res.ok) {
        const errObj = data?.error;
        // SESSION_REVOKED / MFA_REQUIRED short-circuit — same rationale as
        // doRequest(); MSAL silent-refresh would defeat the revocation and
        // can't elevate MFA without interaction.
        if (res.status === 401 && errObj?.code === 'SESSION_REVOKED') {
            handleSessionRevoked();
            throw new ApiError(res.status, errObj.message ?? 'Session revoked.', errObj.details);
        }
        if (res.status === 403 && errObj?.code === 'MFA_REQUIRED') {
            handleMfaRequired();
            throw new ApiError(res.status, errObj.message ?? 'MFA required.', errObj.details);
        }
        // Same one-shot 401 retry as request() — see doRequest() above for
        // why. Form uploads can't be retried trivially in the browser
        // (FormData is consumed by fetch), but FormData itself is reusable
        // — just pass it again with the refreshed bearer.
        if (res.status === 401 && !forceRefresh) {
            return requestForm(path, form, /*forceRefresh*/ true);
        }
        throw new ApiError(res.status, errObj?.message ?? `HTTP ${res.status}`, errObj?.details ?? data);
    }
    return data;
}
async function requestBlob(path, forceRefresh = false) {
    // Binary GET — used for CSV template downloads, PDF exports, etc.
    // Same auth as the JSON helpers; can't reuse request() because it
    // assumes a JSON or text body and would corrupt binary content.
    const auth = await authHeader(forceRefresh);
    const res = await fetch(`${BASE}${path}`, { method: 'GET', headers: { ...auth } });
    if (!res.ok) {
        // SESSION_REVOKED / MFA_REQUIRED short-circuit (same as request()).
        // We can't easily parse the error envelope from a binary 401 body,
        // but the header-only check below is enough: any 401 → assume not
        // a silent-refresh-recoverable case and let the retry path handle
        // it; doRequest()'s code-aware handler covers the JSON path.
        if (res.status === 401 && !forceRefresh) {
            return requestBlob(path, /*forceRefresh*/ true);
        }
        let detail = null;
        try {
            detail = await res.text();
        }
        catch { /* binary error body */ }
        throw new ApiError(res.status, `HTTP ${res.status}`, detail);
    }
    return res.blob();
}
async function requestPostBlob(path, body, forceRefresh = false) {
    // Binary POST — used for report exports (Excel/PDF) where the filter spec
    // rides in a JSON body but the response is a file. Same auth + 401-retry as
    // requestBlob(); can't reuse request() (it assumes a JSON/text response body).
    const auth = await authHeader(forceRefresh);
    const res = await fetch(`${BASE}${path}`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify(body ?? {}),
    });
    if (!res.ok) {
        if (res.status === 401 && !forceRefresh) {
            return requestPostBlob(path, body, /*forceRefresh*/ true);
        }
        let detail = null;
        try {
            detail = await res.text();
        }
        catch { /* binary error body */ }
        throw new ApiError(res.status, `HTTP ${res.status}`, detail);
    }
    return res.blob();
}
export const api = {
    get: (path) => request('GET', path),
    getBlob: (path) => requestBlob(path),
    postBlob: (path, body) => requestPostBlob(path, body),
    post: (path, body, headers) => request('POST', path, body, headers),
    postForm: (path, form) => requestForm(path, form),
    patch: (path, body, headers) => request('PATCH', path, body, headers),
    put: (path, body) => request('PUT', path, body),
    del: (path) => request('DELETE', path),
};
// Public (no auth) — for CSAT token + vendor secure-link endpoints.
// Surface the backend error envelope's message (e.g. "No customer email on file
// to send the code to.") instead of a bare "HTTP 422", mirroring the
// authenticated client so the toast tells the user what to do.
async function publicError(res) {
    let message = `HTTP ${res.status}`;
    let details;
    try {
        const data = await res.json();
        const errObj = data?.error;
        if (errObj?.message)
            message = errObj.message;
        details = errObj?.details ?? data;
    }
    catch {
        /* non-JSON / opaque body — keep the HTTP-status fallback */
    }
    return new ApiError(res.status, message, details);
}
export async function publicGet(path) {
    const res = await fetch(`${BASE}${path}`);
    if (!res.ok)
        throw await publicError(res);
    return res.json();
}
export async function publicPost(path, body) {
    const res = await fetch(`${BASE}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    if (!res.ok)
        throw await publicError(res);
    return res.json();
}
export async function publicPut(path, body) {
    const res = await fetch(`${BASE}${path}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    if (!res.ok)
        throw await publicError(res);
    return res.json();
}
